import { forwardRef, useImperativeHandle, useLayoutEffect, useRef } from 'react';
import { CategoryBarData, SubCategoryBarOptions, SubSubCategoryBarOptions } from 'shared/types/product/Category';
import Link from 'components/commercetools-ui/atoms/link';
import { trackMenuNavigation } from 'helpers/analytics/ga4Events';
import { cleanString, showBadge, toKebabCase } from 'helpers/utils/util';
import { getDynamicMegaMenuStyle, getFilteredSubCategoriesWithValidSubSubs, getStackedSubCategories } from './helper';
import { isValidCategoryType } from '../../helpers';

interface Props {
  isOpen: boolean;
  shouldFocusFirstItem?: boolean;
  category?: CategoryBarData;
  anchorEl: HTMLElement | null;
  onClick: () => void;
  onMouseLeave: () => void;
  onMouseEnter: () => void;
  onTabOutForward?: () => boolean;
  onTabOutBackward?: () => boolean;
  onEscape?: () => void;
}

export const FOCUSABLE_SELECTOR = 'a[href], button, [tabindex]:not([tabindex="-1"])';

const MegaMenu = forwardRef<HTMLDivElement, Props>(
  (
    {
      isOpen,
      shouldFocusFirstItem = false,
      category,
      anchorEl,
      onClick,
      onMouseLeave,
      onMouseEnter,
      onTabOutForward,
      onTabOutBackward,
      onEscape,
    },
    forwardedRef,
  ) => {
    const menuRef = useRef<HTMLDivElement>(null);
    useImperativeHandle(forwardedRef, () => menuRef.current as HTMLDivElement);

    const rect = anchorEl?.getBoundingClientRect();
    const viewportWidth = window?.innerWidth;
    let left = rect?.left ?? 0;
    const top = (rect?.bottom ?? 0) + 8;

    // After render we adjust if overflowing
    useLayoutEffect(() => {
      if (!menuRef.current || !isOpen) return;

      const menuWidth = menuRef.current.offsetWidth;

      if (left + menuWidth > viewportWidth) {
        left = viewportWidth - menuWidth - 16;
      }

      if (left < 16) {
        left = 16;
      }

      menuRef.current.style.left = `${left}px`;

      if (shouldFocusFirstItem) {
        requestAnimationFrame(() => {
          const firstMenuItem = menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]');

          firstMenuItem?.focus();
        });
      }
    }, [anchorEl, isOpen, shouldFocusFirstItem]);

    if (!isOpen || !category || !anchorEl) return null;

    const cols = category?.subcategoryOptions?.length;
    const isCTALink = isValidCategoryType(category?.ctaLink);

    const columnSubCategories = getFilteredSubCategoriesWithValidSubSubs(category.subcategoryOptions);

    const stackedSubCategories = getStackedSubCategories(category.subcategoryOptions);
    const renderSubCategory = (
      sub: SubCategoryBarOptions,
      idx: number,
      onClick: () => void,
      stacked: boolean = false,
    ) => {
      const isCategoryLink = isValidCategoryType(sub?.subcategoryUrls);
      const badge = sub?.badgeConfigs?.[0];
      const badgeVisible = showBadge(badge?.badgeAvailableFrom, badge?.badgeExpiresOn);
      return (
        <div
          key={idx}
          className={`${stacked ? 'mb-10' : 'mb-20'} break-inside-avoid`}
          data-ffid={`ctr-subcat-parent-${sub?.subcategoryNameFFID}`}
        >
          <div className="flex flex-col gap-8">
            {isCategoryLink ? (
              <Link
                className="flex items-center text-14 font-bold text-[#018463]"
                link={sub?.subcategoryUrls ?? '#'}
                role="menuitem"
                tabIndex={0}
                considerQueryParams
                data-ffid={`lnk-subcat-sub-${sub?.subcategoryNameFFID}`}
                onClick={() => {
                  trackMenuNavigation('Category Bar', sub?.subcategoryName ?? '', sub?.subcategoryUrls?.link ?? '#');
                }}
                aria-label={`link ${sub?.subcategoryName}`}
              >
                <span
                  className="hover:underline"
                  data-ffid={`lbl-subcat-label-${sub?.subcategoryNameFFID}`}
                >
                  {sub?.subcategoryName}
                </span>
                {badgeVisible && badge && (
                  <span
                    className="ml-8 inline-block truncate rounded-[34px] border-none px-8 leading-5 no-underline"
                    data-ffid={`lbl-badge-${sub?.subcategoryNameFFID}-${idx}`}
                    style={{
                      fontSize: Number(badge.badgeTextSize),
                      color: badge.badgeTextColor,
                      backgroundColor: badge.badgeBgColor,
                      fontWeight: badge.badgeFontWeight,
                    }}
                  >
                    {badge.badgeText}
                  </span>
                )}
              </Link>
            ) : (
              <p className="flex cursor-default items-center text-14 font-bold text-[#018463]">
                {sub?.subcategoryName}
                {badgeVisible && badge && (
                  <span
                    className="ml-8 inline-block truncate rounded-[34px] border-none px-8 leading-5"
                    data-ffid={`lbl-badge-${toKebabCase(badge.badgeText ?? '')}`}
                    style={{
                      fontSize: Number(badge.badgeTextSize),
                      color: badge.badgeTextColor,
                      backgroundColor: badge.badgeBgColor,
                      fontWeight: badge.badgeFontWeight,
                    }}
                  >
                    {badge.badgeText}
                  </span>
                )}
              </p>
            )}

            {sub?.subSubCategoryOptions?.map(
              ({ subSubCategoryLink, subSubCategoryName, badgeConfigs, subSubcategoryNameFFID }: SubSubCategoryBarOptions, index) => {
                const isValidLink = isValidCategoryType(subSubCategoryLink);
                const subSubCategorybadge = badgeConfigs?.[0];
                const badgeVisible = showBadge(
                  subSubCategorybadge?.badgeAvailableFrom,
                  subSubCategorybadge?.badgeExpiresOn,
                );

                return isValidLink ? (
                  <Link
                    className="flex items-center text-12 font-normal text-[#333]"
                    link={subSubCategoryLink}
                    onClick={() => {
                      trackMenuNavigation('Category Bar', subSubCategoryName ?? '', subSubCategoryLink?.link ?? '#');
                      onClick();
                    }}
                    variant="menu-item"
                    role="menuitem"
                    considerQueryParams
                    tabIndex={0}
                    aria-label={`link ${subSubCategoryName}`}
                    data-ffid={`mnu-subsubcat-item-${subSubcategoryNameFFID}`}
                  >
                    <span
                      className="hover:underline"
                      data-ffid={`lbl-subsubcat-label-${subSubcategoryNameFFID}`}
                    >
                      {subSubCategoryName}
                    </span>
                    {badgeVisible && subSubCategorybadge && (
                      <span
                        className="ml-8 inline-block truncate rounded-[34px] border-none px-8 leading-5"
                        data-ffid={`lbl-badge-${cleanString(subSubCategorybadge.badgeText ?? '')}`}
                        style={{
                          fontSize: Number(subSubCategorybadge.badgeTextSize),
                          color: subSubCategorybadge.badgeTextColor,
                          backgroundColor: subSubCategorybadge.badgeBgColor,
                          fontWeight: subSubCategorybadge.badgeFontWeight,
                        }}
                      >
                        {subSubCategorybadge.badgeText}
                      </span>
                    )}
                  </Link>
                ) : (
                  <p className="flex cursor-default items-center text-12 font-normal text-[#333]" tabIndex={-1}>
                    <span data-ffid={`lbl-subsubcat-label-${subSubcategoryNameFFID}`}>
                      {' '}
                      {subSubCategoryName}
                    </span>
                    {badgeVisible && subSubCategorybadge && (
                      <span
                        className="ml-8 inline-block truncate rounded-[34px] border-none px-8 leading-5"
                        data-ffid={`lbl-badge-${subSubcategoryNameFFID}-${index}`}
                        style={{
                          fontSize: Number(subSubCategorybadge.badgeTextSize),
                          color: subSubCategorybadge.badgeTextColor,
                          backgroundColor: subSubCategorybadge.badgeBgColor,
                          fontWeight: subSubCategorybadge.badgeFontWeight,
                        }}
                      >
                        {subSubCategorybadge.badgeText}
                      </span>
                    )}
                  </p>
                );
              },
            )}
          </div>
        </div>
      );
    };
    return (
      // Sonar: keydown handler delegates focus management for the child menu links
      // eslint-disable-next-line jsx-a11y/no-static-element-interactions
      <div
        ref={menuRef}
        role="menu"
        aria-haspopup="true"
        aria-orientation="vertical"
        aria-label={`${category?.categoryName} submenu`}
        tabIndex={-1}
        style={{
          position: 'fixed',
          top,
          left,
          zIndex: 50,
        }}
        onKeyDown={(e) => {
          const focusableEls = Array.from(menuRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR) ?? []);

          const currentIndex = focusableEls.indexOf(document.activeElement as HTMLElement);

          switch (e.key) {
            case 'Escape':
              e.preventDefault();
              onEscape?.();
              break;

            case 'ArrowDown': {
              e.preventDefault();
              const nextIndex = currentIndex >= focusableEls.length - 1 ? 0 : currentIndex + 1;

              focusableEls[nextIndex]?.focus();
              break;
            }

            case 'ArrowUp': {
              e.preventDefault();

              const prevIndex = currentIndex <= 0 ? focusableEls.length - 1 : currentIndex - 1;

              focusableEls[prevIndex]?.focus();
              break;
            }

            case 'Tab':
              e.preventDefault();

              if (e.shiftKey) {
                onTabOutBackward?.();
              } else {
                onTabOutForward?.();
              }
              break;
          }
        }}
      >
        <div
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          className="z-50 mt-20 w-max rounded-2xl border border-[#ebebeb] bg-white p-32 pb-20 shadow-[0_0_8px_rgba(0,0,0,0.15)]"
        >
          <div className="flex w-fit gap-20">
            {columnSubCategories.length > 0 && (
              <div style={getDynamicMegaMenuStyle(cols ?? 0, columnSubCategories)}>
                {columnSubCategories?.map((sub, idx) => renderSubCategory(sub, idx, onClick, false))}
              </div>
            )}
            {stackedSubCategories.length > 0 && (
              <div className="flex w-fit flex-col">
                {stackedSubCategories?.map((sub, idx) => renderSubCategory(sub, idx, onClick, true))}
              </div>
            )}
          </div>
          <div>
            {isCTALink ? (
              <Link
                className="mt-10 flex items-center gap-12 text-14 font-bold text-[#018463] hover:underline"
                link={category?.ctaLink ?? '#'}
                role="menuitem"
                tabIndex={0}
                considerQueryParams
                aria-label={`link ${category?.ctaText}`}
                data-ffid={`lnk-subcat-sub-${cleanString(category?.ctaText?.replaceAll(' ', '-') ?? '')}`}
                onClick={() => {
                  trackMenuNavigation('Category Bar', category?.ctaText ?? '', category?.ctaLink?.link ?? '#');
                }}
              >
                {category?.ctaText}
              </Link>
            ) : null}
          </div>
        </div>
      </div>
    );
  },
);

MegaMenu.displayName = 'MegaMenu';

export default MegaMenu;
